import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Loader2, Users, Shield, UserX, UserCheck, Crown, CalendarCheck, CalendarX, Zap } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function MembersDialog({ open, onClose, currentUser }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(null);
  const [calStatus, setCalStatus] = useState(null);
  const [linkingCal, setLinkingCal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ms, st] = await Promise.all([
        api.get("/users"),
        api.get("/oauth/calendar/status").catch(() => ({ data: { connected: false } })),
      ]);
      setMembers(ms.data);
      setCalStatus(st.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const isAdmin = currentUser?.role === "admin";

  const connectCalendar = async () => {
    setLinkingCal(true);
    try {
      const res = await api.get("/oauth/calendar/start");
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore avvio Google");
      setLinkingCal(false);
    }
  };

  const disconnectCalendar = async () => {
    if (!window.confirm("Scollego Google Calendar? Non potrai più partecipare a ZAP finché non lo ricollegherai.")) return;
    try {
      await api.delete("/oauth/calendar/disconnect");
      toast.success("Calendario scollegato");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const doAction = async (userId, action) => {
    setActing(userId + action);
    try {
      await api.post(`/users/${userId}/${action}`);
      toast.success("Aggiornato");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setActing(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="members-dialog" className="max-w-lg rounded-3xl border-[#E8E1D9] bg-white max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="mb-2 inline-flex w-fit items-center gap-2 rounded-full border border-[#E8E1D9] bg-[#FBF9F6] px-3 py-1">
            <Users className="h-3 w-3 text-[#FF7C43]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C87941]">Casa & Calendario</span>
          </div>
          <DialogTitle className="font-display text-2xl font-bold text-[#2D2926]">
            Il tuo calendario + i membri
          </DialogTitle>
          <DialogDescription className="text-[#5C5855]">
            Collega Google Calendar per far parte degli ZAP.
          </DialogDescription>
        </DialogHeader>

        {/* Google Calendar connection banner (for current user) */}
        <div className={`mb-4 rounded-2xl border p-4 ${
          calStatus?.connected
            ? "border-green-200 bg-green-50/60"
            : "border-[#FF7C43]/30 bg-gradient-to-br from-[#FFF4EC] to-white"
        }`}>
          {calStatus?.connected ? (
            <div className="flex items-start gap-3">
              <CalendarCheck className="h-6 w-6 shrink-0 text-green-600 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#2D2926]">Google Calendar collegato ✅</p>
                <p className="text-xs text-[#5C5855] truncate">{calStatus.google_email}</p>
                <button
                  data-testid="disconnect-calendar-button"
                  onClick={disconnectCalendar}
                  className="mt-1 text-[11px] font-semibold text-red-600 hover:underline"
                >
                  Scollega
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <Zap className="h-6 w-6 shrink-0 text-[#FF7C43] mt-0.5" fill="#FF7C43" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#2D2926]">Collega Google Calendar</p>
                <p className="mt-1 text-xs text-[#5C5855]">
                  Come Booking: l'app vedrà quando sei <strong>occupato/a</strong> (mai i dettagli!) per trovare quando siete tutti liberi.
                </p>
                <Button
                  data-testid="connect-calendar-button"
                  onClick={connectCalendar}
                  disabled={linkingCal}
                  size="sm"
                  className="mt-2 rounded-full bg-[#FF7C43] text-white hover:bg-[#E66A35] text-xs"
                >
                  {linkingCal
                    ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    : <CalendarCheck className="mr-1 h-3 w-3" />}
                  Collega il mio Google Calendar
                </Button>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#FF7C43]" /></div>
        ) : members.length === 0 ? (
          <p className="py-6 text-center text-sm text-[#5C5855]">Nessun membro registrato</p>
        ) : (
          <div className="space-y-2 py-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C87941]">Membri della Casa</p>
            {members.map((m) => {
              const isSelf = m.id === currentUser?.id;
              const isRevoked = m.status === "revoked";
              return (
                <div
                  key={m.id}
                  data-testid={`member-row-${m.email}`}
                  className={`flex items-center justify-between gap-3 rounded-2xl border p-3 ${
                    isRevoked ? "border-red-200 bg-red-50/40" : "border-[#E8E1D9] bg-white"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-[#2D2926] truncate">{m.full_name}</p>
                      {m.role === "admin" && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#FF7C43]/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#FF7C43]">
                          <Crown className="h-2.5 w-2.5" /> Admin
                        </span>
                      )}
                      {isSelf && (
                        <span className="rounded-full bg-[#2D2926]/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#2D2926]">
                          Tu
                        </span>
                      )}
                      {isRevoked && (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-red-700">
                          Revocato
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#5C5855] truncate">{m.email}</p>
                  </div>
                  {isAdmin && !isSelf && (
                    <div className="flex gap-1.5 shrink-0">
                      {isRevoked ? (
                        <Button
                          data-testid={`restore-${m.email}`}
                          size="sm"
                          onClick={() => doAction(m.id, "restore")}
                          disabled={acting === m.id + "restore"}
                          className="h-8 gap-1 rounded-full bg-green-600 px-3 text-xs text-white hover:bg-green-700"
                        >
                          {acting === m.id + "restore"
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <UserCheck className="h-3 w-3" />}
                          Ripristina
                        </Button>
                      ) : (
                        <Button
                          data-testid={`revoke-${m.email}`}
                          size="sm"
                          variant="outline"
                          onClick={() => doAction(m.id, "revoke")}
                          disabled={acting === m.id + "revoke"}
                          className="h-8 gap-1 rounded-full border-red-200 px-3 text-xs text-red-700 hover:bg-red-50"
                        >
                          {acting === m.id + "revoke"
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <UserX className="h-3 w-3" />}
                          Revoca
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!isAdmin && (
          <div className="mt-3 flex items-start gap-2 rounded-xl bg-[#FBF9F6] p-3">
            <Shield className="h-4 w-4 shrink-0 text-[#C87941] mt-0.5" />
            <p className="text-xs text-[#5C5855]">
              Solo l'admin può revocare o ripristinare l'accesso dei membri.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
